import type {
  ConsoleThreadListQuery,
  ThreadKind,
  ThreadPriority,
  ThreadStatus,
} from '@central/api';
import type { StatusTone } from '@central/ui';

/** Every status but closed: on hold counts as open. */
const ACTIVE = 'open,waiting_on_us,waiting_on_customer,on_hold';

export const statusOptions: Array<{ value: ThreadStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'waiting_on_us', label: 'Waiting on us' },
  { value: 'waiting_on_customer', label: 'Waiting on customer' },
  { value: 'on_hold', label: 'On hold (waiting on a third party)' },
  { value: 'closed', label: 'Resolved' },
];

/** Priorities, most urgent first. */
export const priorities: ThreadPriority[] = ['urgent', 'high', 'normal', 'low'];

export const priorityChip: Record<ThreadPriority, { tone: StatusTone; label: string }> = {
  urgent: { tone: 'down', label: 'Urgent' },
  high: { tone: 'warn', label: 'High' },
  normal: { tone: 'unk', label: 'Normal' },
  low: { tone: 'unk', label: 'Low' },
};

export const priorityOptions = priorities.map((p) => ({ value: p, label: priorityChip[p].label }));

export const kindChip: Record<ThreadKind, { tone: StatusTone; label: string }> = {
  crash: { tone: 'down', label: 'Crash report' },
  bug: { tone: 'warn', label: 'Bug' },
  feature: { tone: 'vio', label: 'Feature' },
  renewal: { tone: 'blu', label: 'Renewal' },
  license: { tone: 'blu', label: 'License change' },
  question: { tone: 'unk', label: 'Question' },
};

/** The priorities in a `priority` search value, in `priorities` order; unknown ones are dropped. */
export function parsePriorities(v: unknown): ThreadPriority[] {
  if (typeof v !== 'string') return [];
  const want = new Set(v.split(','));
  return priorities.filter((p) => want.has(p));
}

/** Preset lists from the design; every view but Resolved shows active threads only. */
export const inboxViews = {
  open: { label: 'Open', q: { status: ACTIVE } },
  crash: { label: 'Crash reports', q: { status: ACTIVE, kind: 'crash' } },
  bug: { label: 'Bugs', q: { status: ACTIVE, kind: 'bug' } },
  feature: { label: 'Feature requests', q: { status: ACTIVE, kind: 'feature' } },
  licensing: { label: 'Licensing & renewals', q: { status: ACTIVE, kind: 'license,renewal' } },
  question: { label: 'Questions', q: { status: ACTIVE, kind: 'question' } },
  mine: { label: 'Assigned to me', q: { status: ACTIVE, assigneeId: 'me' } },
  unassigned: { label: 'Unassigned', q: { status: ACTIVE, assigneeId: 'none' } },
  closed: { label: 'Resolved', q: { status: 'closed' } },
} satisfies Record<string, { label: string; q: ConsoleThreadListQuery }>;

export type InboxView = keyof typeof inboxViews;

/** Views a reseller has: licensing threads are Rethink's alone. */
export const resellerInboxViews = (Object.keys(inboxViews) as InboxView[]).filter(
  (v) => v !== 'licensing',
);

export interface InboxSearch {
  view?: InboxView;
  /** tier1: with the customer's reseller; tier2: with Rethink. */
  tier?: 'tier1' | 'tier2';
  /** A ticket reference ("GC-1042") or words in the subject; searches every status. */
  q?: string;
  customerId?: string;
  /** Rethink staff only: a reseller's id, or `none` for the customers Rethink supports directly. */
  resellerId?: string;
  unread?: boolean;
  /** A staff tag; combines with the view and the other filters. */
  tag?: string;
  /** Comma-separated priorities, e.g. "urgent,high"; absent is every priority. */
  priority?: string;
  /** A thread to open: the inbox redirects to its ticket page. */
  thread?: string;
}

/** The list filters a ticket page carries, so its back link returns to the same list. */
export type InboxListSearch = Omit<InboxSearch, 'thread'>;

/** The list filters of search, without the thread to open. */
export function listSearchOf(search: InboxSearch): InboxListSearch {
  return {
    view: search.view,
    tier: search.tier,
    q: search.q,
    customerId: search.customerId,
    resellerId: search.resellerId,
    unread: search.unread,
    tag: search.tag,
    priority: search.priority,
  };
}

/** The views offered to a reseller or to Rethink staff. */
export function viewsFor(reseller: boolean): InboxView[] {
  return reseller ? resellerInboxViews : (Object.keys(inboxViews) as InboxView[]);
}

/**
 * The view search shows (an unknown or unoffered one is Open), the filters
 * beside the view, and the thread list query they make. A search looks across
 * every status and kind, so a resolved ticket is found by its reference.
 */
export function inboxListQuery(search: InboxListSearch, reseller: boolean) {
  const view: InboxView =
    search.view && viewsFor(reseller).includes(search.view) ? search.view : 'open';
  const filters = {
    customerId: search.customerId,
    // The partner routes have no reseller filter: a reseller sees only its own.
    resellerId: reseller ? undefined : search.resellerId,
    unread: search.unread || undefined,
    tag: search.tag,
    priority: search.priority,
    tier: search.tier,
  };
  const listQuery: ConsoleThreadListQuery = search.q
    ? { q: search.q, ...filters }
    : { ...inboxViews[view].q, ...filters };
  return { view, filters, listQuery };
}
